import { action, observable } from 'mobx';
import { FormulaFormVM } from '../template/FormulaFormVM';
import { HydraulicFormulaController } from 'cd-eft/services/eftSetApi';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { convertStringFractionToDecimal } from 'cd-eft/utils/mathUtilities';

interface ISubsurfaceDrainagelForm {
  drainageCoefficient: number;
  manningsN: number;
  slopeList: Array<number>;
  areaList: Array<number>;
}
interface ICalculationResult {}
export class SubsurfaceDrainageVM extends FormulaFormVM {
  readonly formLabelsForExport;
  readonly resultLabelsForExport;
  readonly initialCalculationResults: ICalculationResult = {};
  filteredArr: any;
  constructor(props) {
    super(props);
    // Any Form specific contructor code. Otherwise, remove contructor - use parent.
    this.disablePrintBtn = true;
    this.formLabelsForExport = {
      drainageCoefficient: 'Drainage Coefficient (in/day)',
      manningsN: "Manning's n",
    };
    this.resultLabelsForExport = {
      slope: 'Slope (ft/ft)',
      acres: 'Acres',
      qreq: 'Flow Q (cfs)',
      qavail: 'Capacity (cfs)',
      acresAvail: 'Max Acres',
      diameter: 'Diameter (in)',
      velocity: 'Velocity (fps)',
      // pipelineName: "",
    };
    this.helpLink =
      'https://usdagcc.sharepoint.com/:b:/r/sites/FPAC-NRCS-EngineeringPlanningAnalysisandDesignSoftwareEPAD/Shared%20Documents/General/NETS-Support%20Documents/Formulas%20Documentation/Help%20Select%20n%20Value.pdf?csf=1&web=1&e=ssYi6q';
  }
  @observable calculationResults: ICalculationResult = this.initialCalculationResults;
  @observable savedsubsurfaceDrainageFormData: any = [];
  @observable subsurfaceDrainageOptions: any = [];
  @observable headers: any = ['Area', 'Slope'];
  @observable resultHeaders: any = ['Flow Q (cfs)', 'Capacity (cfs)', 'Max Acers', 'Diameter(in)', 'Velocity(fps)'];
  @observable resultsRows: any = [
    {
      acres: "0",
      acresAvail: "0",
      diameter: "0",
      qavail: "0",
      qreq: "0",
      slope: "0",
      velocity: "0",
    },
  ];
  @observable formValidation = {
    hasError: true,
    drainageCoefficient: false,
    manningsN: false,
    formErrors: {
      drainageCoefficient: '',
      manningsN: '',
    },
  };

  @observable subsurfaceDrainageFormData: ISubsurfaceDrainagelForm = {
    drainageCoefficient: 0,
    manningsN: 0,
    slopeList: [],
    areaList: [],
  };

  @action.bound generateEmptyRows() {
    this.resultsRows.push(
      ...Array.from({ length: 10 }, () => ({
        acres: 0,
        acresAvail: 0,
        diameter: 0,
        // pipelineName: "",
        qavail: 0,
        qreq: 0,
        slope: 0,
        velocity: 0,
      }))
    );

    this.subsurfaceDrainageFormData.slopeList.push(
      ...Array.from({ length: 10 - this.subsurfaceDrainageFormData.slopeList.length }, () => 0) 
    );
    
    this.subsurfaceDrainageFormData.areaList.push(
      ...Array.from({ length: 10 - this.subsurfaceDrainageFormData.areaList.length }, () =>0)
    );
  }

  @action.bound handleInputChange(event, index?) {
    const { name } = event.target;
    let value = event.target.value;
    if (value.length > 7) {
      return;
    }
    switch (name) {
      case 'drainage-coefficient':
        this.subsurfaceDrainageFormData.drainageCoefficient = value;
        break;
      case 'mannings-n':
        this.subsurfaceDrainageFormData.manningsN = value;
        break;
      case `table-inputs-slope-${index}`:
        if (value === '') {
          value = 0;
        }
        this.subsurfaceDrainageFormData.slopeList[index] = value;
        break;
      case `table-inputs-area-${index}`:
        if (value === '') {
          value = 0;
        }
        this.subsurfaceDrainageFormData.areaList[index] = (value);
        break;
    }
  }

  @action.bound handleOnKeyDown(event) {
    const invalidKeys = ['e', 'E', '+', '-'];
    if (invalidKeys.includes(event.key)) {
      event.preventDefault();
    }
    else if (event.code === 'Escape') {
      // Allows to blur the inputs so screen reader arrow controls can read the non-input columns.
      const focused = document.activeElement as HTMLElement;
      if (focused)
        focused.blur();
    }
  }

  @action.bound setFixedValue(name, fixed, index?) {
    const event = {
      target: {
        name: name,
        value: fixed,
      },
    };
    this.handleInputChange(event, index);
  }

  @action.bound async validateAllInputsPass() {}

  @action.bound async validateInput(name, value, index?) {
    let val;
    if (value !== undefined && value !== '') {
      if (name === 'mannings-n' || name === 'drainage-coefficient' || name.includes('table-inputs-slope-')) {
        val = Number.isNaN(value) ? '': this.roundingHelperNumber(parseFloat(value), 4);
      } else {
        val = this.roundingHelperNumber(parseFloat(value), 2);
      }
    } else {
      val = 0;
    }
    if (value !== undefined && value !== '') {
      this.setFixedValue(name, val, index);
    }
    switch (name) {
      case 'drainage-coefficient':
        if (val <= 0 || val === '') {
          this.formValidation.drainageCoefficient = true;
          this.formValidation.formErrors.drainageCoefficient = ' ';
          this.formValidation.drainageCoefficient = true;
          await MessageCenter.addNotificationFromCode('Common_26');
        } else {
          this.formValidation.drainageCoefficient = false;
          this.formValidation.formErrors.drainageCoefficient = '';
          this.formValidation.drainageCoefficient = false;
        }
        break;
      case 'mannings-n':
        if (val < 0.0001 || val > 1 || value === '') {
          this.formValidation.manningsN = true;
          this.formValidation.formErrors.manningsN = ' ';
          this.formValidation.manningsN = true;
          await MessageCenter.addNotificationFromCode('Formula_02');
        } else {
          this.formValidation.manningsN = false;
          this.formValidation.formErrors.manningsN = '';
          this.formValidation.manningsN = false;
        }
        break;
        case `${name}`:
          if(name.includes("area")){
            if (val <= 0 || val === '') {
              return
            } else {
              this.subsurfaceDrainageFormData.areaList[index] = this.roundingHelperNumber((value), 2);
            }
          } else {
          if (val <= 0 || val === '') {
            return       
          } else {
            this.subsurfaceDrainageFormData.slopeList[index] = this.roundingHelperNumber((value), 4);   
          }
        }
          break;
    }
  }

  @action.bound subsurfaceDrainageErrorClass(field) {
    const errorMessage = this.formValidation.formErrors[field];
    return errorMessage?.length === 0 ? '' : 'usa-input--error';
  }

  @action.bound findIndexAllZeros = (arr) => arr.findIndex((_, i) => arr.slice(i + 1).every((n) => n === 0));

  get validateRequiredInputs() {
    if (
      this.formValidation.drainageCoefficient ||
      this.formValidation.manningsN 
    ) {
      return false;
    }
      return true;
  }

  @action.bound async validateReqInput() {
    await this.validateInput('drainage-coefficient', this.subsurfaceDrainageFormData.drainageCoefficient);
    await this.validateInput('mannings-n', this.subsurfaceDrainageFormData.manningsN);
  }

  @action.bound async computeSubsurfaceDrainage() {
    this.disablePrintBtn = true;
    this.disableComputeBtn = true;
    this.isLoading = true;
    MessageCenter.blockCalls([]);
    await this.validateReqInput();
    
    if (this.validateRequiredInputs) {
      let reqBody = this.subsurfaceDrainageFormData;
      const maxLength = Math.max(this.findIndexAllZeros(reqBody.slopeList), this.findIndexAllZeros(reqBody.areaList));
      reqBody.slopeList = reqBody.slopeList.map(value => (value));
      reqBody.slopeList = reqBody.slopeList.slice(0, maxLength + 1);
      reqBody.areaList = reqBody.areaList.slice(0, maxLength + 1);
      try {
        const response = await HydraulicFormulaController.calculateSubsurfaceDrainageUsingPOST(reqBody);
        if (response && response.data) {
          if(response.data.validationPass){

            let updatedRows = response.data.pipelines?.map((obj) => {
              let { pipelineName, ...rest } = obj;
              return Object.fromEntries(
                Object.entries(rest).map(([key, value]) => [key, key !== 'slope' ? this.roundingHelperNumber(value, 4) : value])
              );
            });
            this.resultsRows = updatedRows;
            this.hasResults = true;
            this.disablePrintBtn = false;
          } else{
            this.resultsRows = [];
            this.resultsRows.push(
              ...Array.from({ length: 10 }, () => ({
                acres: 0,
                acresAvail: 0,
                diameter: 0,
                qavail: 0,
                qreq: 0,
                slope: 0,
                velocity: 0,
              }))
            );
            if (this.hasErrorContent(response.data.errorResponse)) {
              MessageCenter.addNotification(response.data.errorResponse!.errorMessage!, response.data.errorResponse!.errorType!, response.data.errorResponse!.errorCode,
                response.data.errorResponse?.urlPath ? [{
                  text: 'Help',
                  callback: () => {
                    window.open(response.data.errorResponse?.urlPath, '_blank');
                  }
                }] : undefined)
              this.calculationResults = [];
            this.hasResults = false;
            this.disablePrintBtn = true;

          }
        }}
      } catch (error) {
        console.error(error);
      }
    }else{
    MessageCenter.unblockCalls();
    MessageCenter.resetMessageCenter();
    await MessageCenter.addNotificationFromCode('Common_26')
    this.hasResults = false;
    this.disablePrintBtn = true;
  }
    this.resultsRows.push(
      ...Array.from({ length: 10 - this.resultsRows.length }, () => ({
        acres: 0,
        acresAvail: 0,
        diameter: 0,
        qavail: 0,
        qreq: 0,
        slope: 0,
        velocity: 0,
      }))
    );
    this.subsurfaceDrainageFormData.slopeList.push(
      ...Array.from({ length: 10 - this.subsurfaceDrainageFormData.slopeList.length }, () => 0)
    );
    this.subsurfaceDrainageFormData.areaList.push(
      ...Array.from({ length: 10 - this.subsurfaceDrainageFormData.areaList.length }, () => 0)
    );

    this.savedsubsurfaceDrainageFormData = { ...this.subsurfaceDrainageFormData };
    this.disableComputeBtn = false;
    this.isLoading = false;
    MessageCenter.unblockCalls();
  }

  @action.bound launchAndExport() {
    this.disablePrintBtn = true;
    this.exportedInputs = [];
    this.exportedResults = [];

    for (const input in this.savedsubsurfaceDrainageFormData) {
      if (input === 'slopeList' || input === 'areaList') {
        break;
      }
      this.exportedInputs.push({
        label: this.formLabelsForExport[input],
        value: this.savedsubsurfaceDrainageFormData[input],
      });
    }

    let filteredArr = this.resultsRows?.filter((obj) => !Object.values(obj).every((value) => value === 0));
    const updatedFilteredArrDecimalPlaces = filteredArr.map(item => ({
      ...item,
      slope: this.roundingHelperString(parseFloat(item.slope), 4),
      acres: this.roundingHelperString(parseFloat(item.acres), 2),
      acresAvail: this.roundingHelperString(parseFloat(item.acresAvail), 2),
      diameter: this.roundingHelperString(parseFloat(item.diameter), 2),
      velocity: this.roundingHelperString(parseFloat(item.velocity), 2),
      qreq: this.roundingHelperString(parseFloat(item.qreq), 2),
      qavail: this.roundingHelperString(parseFloat(item.qavail), 2)
    }));
    if(filteredArr.length === 0){
      filteredArr = [{acres: 0,
        acresAvail: "0.00",
        diameter: "0.00",
        qavail: "0.00",
        qreq: "0.00",
        slope: "0.0000",
        velocity: "0.00"}]
    }
    delete this.resultLabelsForExport.pipelineName;
    const tableHeaders = Object.values(this.resultLabelsForExport).join(', ');
    this.exportedResults = updatedFilteredArrDecimalPlaces.map((item) => {
      return { label: tableHeaders, value: item };
    });
    this.printFormula();
    this.disablePrintBtn = false;
  }

  @action.bound initializeDefaultValues() {
    const updateDecimalPlaces = (obj) =>{
      if (obj.areaList) {
        obj.areaList = obj.areaList.map(value => this.roundingHelperString(value, 2));
      }
      if (obj.slopeList) {
        obj.slopeList = obj.slopeList.map(value => this.roundingHelperString(value, 4));
      }
      if (obj.manningsN) {
        obj.manningsN = this.roundingHelperString(obj.manningsN, 4)
      }
      if (obj.drainageCoefficient) {
        obj.drainageCoefficient = 1;
      }
      return obj;
    }
    this.subsurfaceDrainageOptions =  this.defaultValues.drainageCoefficient.length ? (this.defaultValues.drainageCoefficient as string[]).map((value, index) => ({
      value: convertStringFractionToDecimal(value),
      name: value,
    })): null;
    this.subsurfaceDrainageFormData = updateDecimalPlaces(this.defaultValues);
    this.generateEmptyRows();
  }
}
